import { useCallback, useEffect, useState } from 'react';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { CANVAS_CREATIVE_ID, USER_TEXT_TRACKING_LENGTH } from 'site-modules/canvas/constants/canvas';
import { ellipsisTruncate } from 'client/utils/string-utils';
import { fireNoStreamIdTracking } from 'site-modules/canvas/utils/canvas-tracking';
import { useResponseStream } from 'site-modules/canvas/hooks/use-response-stream';

export const checkStreamCompleteness = data => ['stop', 'finish'].includes(data.finish);

export function useStartMessages(startMessage) {
  const [streamDone, setStreamDone] = useState(false);
  const [responseId, setResponseId] = useState(null);

  const { text, userText, responsePromise, generatedResponseId } = startMessage || {};

  const trackingValue = userText ? `search=${ellipsisTruncate(userText, USER_TEXT_TRACKING_LENGTH)}` : undefined;

  const handleError = useCallback(() => {
    setStreamDone(true);
  }, []);

  const onResponseError = useCallback(() => {
    handleError();
  }, [handleError]);

  const onStreamComplete = useCallback(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: {
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        action_name: TrackingConstant.VIEW_SEARCH_RESULT,
        subaction_name: TrackingConstant.VIEW_AUTO_COMPLETE_RESULT,
        creative_id: CANVAS_CREATIVE_ID,
        value: trackingValue,
        resp_uuid: responseId,
      },
    });

    setStreamDone(true);
  }, [responseId, trackingValue]);

  const [responseText] = useResponseStream(responseId, {
    bodyStream: null,
    onComplete: onStreamComplete,
    onError: onResponseError,
    creativeId: CANVAS_CREATIVE_ID,
  });

  useEffect(() => {
    if (responsePromise) {
      responsePromise
        .then(id => {
          setResponseId(id);
        })
        .catch(error => {
          fireNoStreamIdTracking(error, CANVAS_CREATIVE_ID, generatedResponseId);
          /* istanbul ignore else */
          if (error?.name !== 'AbortError') {
            handleError();
          }
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsePromise, text]);

  return { isThinking: !streamDone && !text, suggestedMessages: text || responseText };
}
